/**
 * Normalized data-store slices keep entities in a flat id→record map on the slice root.
 * Some selectors/sagas still read `.data`; this helper supports both shapes.
 * Root-level entries win over nested `.data` (e.g. after addItem following a fetch merge).
 *
 * Results are memoized per slice reference so useSelector does not see a new object
 * when Redux state has not changed (avoids unnecessary rerenders / React-Redux warnings).
 * @param {object|undefined|null} storeSlice
 * @returns {Record<string, object>}
 */

const EMPTY_ENTITY_MAP = Object.freeze({})
const entityMapCache = new WeakMap()

export function getDataStoreEntityMap (storeSlice) {
  if (storeSlice == null || typeof storeSlice !== 'object') {
    return EMPTY_ENTITY_MAP
  }

  const cached = entityMapCache.get(storeSlice)
  if (cached) {
    return cached
  }

  const isEntityRecord = value =>
    value != null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    (value.id != null || value.name != null)

  const isEntityMap = obj => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false
    const keys = Object.keys(obj)
    return keys.length === 0 || isEntityRecord(obj[keys[0]])
  }

  const nested = isEntityMap(storeSlice.data) ? storeSlice.data : EMPTY_ENTITY_MAP
  const rootEntries = Object.entries(storeSlice).filter(
    ([key, value]) => key !== 'data' && isEntityRecord(value)
  )

  let result
  if (Object.keys(nested).length === 0 && rootEntries.length === 0) {
    result = isEntityMap(storeSlice) ? storeSlice : EMPTY_ENTITY_MAP
  } else if (
    nested !== EMPTY_ENTITY_MAP &&
    rootEntries.length === 0 &&
    isEntityMap(storeSlice.data)
  ) {
    // Prefer the nested map reference when root has no entity overrides.
    result = nested
  } else {
    result = {
      ...nested,
      ...Object.fromEntries(rootEntries)
    }
  }

  entityMapCache.set(storeSlice, result)
  return result
}
